import { RefObject, useEffect } from 'react';

interface AutoScrollOptions {
  speed?: number;
  pauseOnHover?: boolean;
}

const defaultOptions: Required<AutoScrollOptions> = {
  speed: 0.5,
  pauseOnHover: true,
};

export function useAutoHorizontalScroll<T extends HTMLElement>(
  ref: RefObject<T>,
  options: AutoScrollOptions = defaultOptions
) {
  const { speed, pauseOnHover } = { ...defaultOptions, ...options };

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    let frameId: number;
    let isPaused = false;

    const step = () => {
      if (node && !isPaused) {
        const maxScroll = node.scrollWidth - node.clientWidth;
        if (node.scrollLeft >= maxScroll) {
          node.scrollLeft = 0;
        } else {
          node.scrollLeft += speed;
        }
      }
      frameId = requestAnimationFrame(step);
    };

    frameId = requestAnimationFrame(step);

    const handleMouseEnter = () => { if (pauseOnHover) isPaused = true; };
    const handleMouseLeave = () => { if (pauseOnHover) isPaused = false; };
    const handleTouchStart = () => { isPaused = true; };
    const handleTouchEnd = () => {
      setTimeout(() => { isPaused = false; }, 1500);
    };

    node.addEventListener('mouseenter', handleMouseEnter);
    node.addEventListener('mouseleave', handleMouseLeave);
    node.addEventListener('touchstart', handleTouchStart, { passive: true });
    node.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      cancelAnimationFrame(frameId);
      node.removeEventListener('mouseenter', handleMouseEnter);
      node.removeEventListener('mouseleave', handleMouseLeave);
      node.removeEventListener('touchstart', handleTouchStart);
      node.removeEventListener('touchend', handleTouchEnd);
    };
  }, [ref, speed, pauseOnHover]);
}
